export const NUTRITION_MACRO_KEYS = ["energy_amount", "proteins_amount", "fat_amount", "carbohydrates_amount"] as const;
export const MEASURE_UNITS = ["GRAM", "MILLILITER", "KILOGRAM", "LITER"] as const;

export type NutritionMacroKey = (typeof NUTRITION_MACRO_KEYS)[number];
export type MeasureUnit = (typeof MEASURE_UNITS)[number];
export type WeightDimension = "mass" | "volume";

export type NutritionAmounts = {
	energy_amount: number;
	proteins_amount: number;
	fat_amount: number;
	carbohydrates_amount: number;
	/** Граммы после нормализации GRAM/KILOGRAM. */
	weightMass: number;
	/** Миллилитры после нормализации MILLILITER/LITER. */
	weightVolume: number;
};

export type NutritionSource = Partial<{
	energy_amount: number | null;
	proteins_amount: number | null;
	fat_amount: number | null;
	carbohydrates_amount: number | null;
	weight: number | null;
	measure_unit: string | null;
}> & {
	id?: string;
	is_default?: boolean;
};

const UNIT_CANONICAL: Record<MeasureUnit, { dimension: WeightDimension; factor: number }> = {
	GRAM: { dimension: "mass", factor: 1 },
	KILOGRAM: { dimension: "mass", factor: 1000 },
	MILLILITER: { dimension: "volume", factor: 1 },
	LITER: { dimension: "volume", factor: 1000 },
};

/** `null` и неизвестные значения из iiko считаем граммами. */
export const normalizeMeasureUnit = (unit?: string | null): MeasureUnit => {
	const normalized = unit?.trim().toUpperCase();
	if (normalized && normalized in UNIT_CANONICAL) return normalized as MeasureUnit;
	return "GRAM";
};

export type NutritionModifierInput = NutritionSource & {
	amount?: number | null;
	sizes?: NutritionSource[] | null;
	size?: NutritionSource | string | string[] | null;
};

export type NutritionGroupInput = {
	modifiers?: NutritionModifierInput[] | null;
};

export type NutritionProductInput = NutritionSource & {
	sizes?: NutritionSource[] | null;
	portion_nutrition?: NutritionSource | null;
	modifiers?: NutritionModifierInput[] | null;
};

export type CalculateNutritionsInput = {
	product?: NutritionProductInput | null;
	selectedSize?: NutritionSource | string | null;
	modifiers?: NutritionModifierInput[] | null;
	groupModifiersSelected?: NutritionGroupInput[] | null;
	/** Кол-во порций блюда. Для записи в корзину оставлять 1 — сумму умножают отдельно. */
	amount?: number | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

export const emptyNutritions = (): NutritionAmounts => ({
	energy_amount: 0,
	proteins_amount: 0,
	fat_amount: 0,
	carbohydrates_amount: 0,
	weightMass: 0,
	weightVolume: 0,
});

export const toNutritionNumber = (value: number | null | undefined): number => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : 0;
};

export const toCanonicalWeight = (weight?: number | null, unit?: string | null): Pick<NutritionAmounts, "weightMass" | "weightVolume"> => {
	const value = toNutritionNumber(weight);
	if (value === 0) return { weightMass: 0, weightVolume: 0 };

	const spec = UNIT_CANONICAL[normalizeMeasureUnit(unit)];
	const canonical = value * spec.factor;
	return spec.dimension === "mass" ? { weightMass: canonical, weightVolume: 0 } : { weightMass: 0, weightVolume: canonical };
};

export const hasNutritionValue = (value: number | null | undefined): boolean => toNutritionNumber(value) !== 0;

export const hasVisibleMacroNutritions = (nutritions: NutritionAmounts): boolean => NUTRITION_MACRO_KEYS.some((key) => hasNutritionValue(nutritions[key]));

export const hasVisibleWeight = (nutritions: NutritionAmounts): boolean =>
	toNutritionNumber(nutritions.weightMass) > 0 || toNutritionNumber(nutritions.weightVolume) > 0;

export const pickNutritionSource = (source?: NutritionSource | null): NutritionAmounts => ({
	energy_amount: toNutritionNumber(source?.energy_amount),
	proteins_amount: toNutritionNumber(source?.proteins_amount),
	fat_amount: toNutritionNumber(source?.fat_amount),
	carbohydrates_amount: toNutritionNumber(source?.carbohydrates_amount),
	...toCanonicalWeight(source?.weight, source?.measure_unit),
});

export const addNutritions = (left: NutritionAmounts, right: NutritionAmounts): NutritionAmounts => ({
	energy_amount: toNutritionNumber(left.energy_amount) + toNutritionNumber(right.energy_amount),
	proteins_amount: toNutritionNumber(left.proteins_amount) + toNutritionNumber(right.proteins_amount),
	fat_amount: toNutritionNumber(left.fat_amount) + toNutritionNumber(right.fat_amount),
	carbohydrates_amount: toNutritionNumber(left.carbohydrates_amount) + toNutritionNumber(right.carbohydrates_amount),
	weightMass: toNutritionNumber(left.weightMass) + toNutritionNumber(right.weightMass),
	weightVolume: toNutritionNumber(left.weightVolume) + toNutritionNumber(right.weightVolume),
});

export const scaleNutritions = (nutritions: NutritionAmounts, amount: number): NutritionAmounts => {
	const multiplier = toNutritionNumber(amount);
	return {
		energy_amount: toNutritionNumber(nutritions.energy_amount) * multiplier,
		proteins_amount: toNutritionNumber(nutritions.proteins_amount) * multiplier,
		fat_amount: toNutritionNumber(nutritions.fat_amount) * multiplier,
		carbohydrates_amount: toNutritionNumber(nutritions.carbohydrates_amount) * multiplier,
		weightMass: toNutritionNumber(nutritions.weightMass) * multiplier,
		weightVolume: toNutritionNumber(nutritions.weightVolume) * multiplier,
	};
};

export const toNutritionsPer100g = (nutritions: NutritionAmounts): NutritionAmounts => {
	const weight = toNutritionNumber(nutritions.weightMass) + toNutritionNumber(nutritions.weightVolume);
	if (weight <= 0) return nutritions;
	const factor = 100 / weight;
	return {
		...nutritions,
		energy_amount: toNutritionNumber(nutritions.energy_amount) * factor,
		proteins_amount: toNutritionNumber(nutritions.proteins_amount) * factor,
		fat_amount: toNutritionNumber(nutritions.fat_amount) * factor,
		carbohydrates_amount: toNutritionNumber(nutritions.carbohydrates_amount) * factor,
	};
};

export const toCartNutritionFields = (nutritions: NutritionAmounts) => ({
	energy_amount: toNutritionNumber(nutritions.energy_amount),
	proteins_amount: toNutritionNumber(nutritions.proteins_amount),
	fat_amount: toNutritionNumber(nutritions.fat_amount),
	carbohydrates_amount: toNutritionNumber(nutritions.carbohydrates_amount),
	weight: toNutritionNumber(nutritions.weightMass),
	weight_mass: toNutritionNumber(nutritions.weightMass),
	weight_volume: toNutritionNumber(nutritions.weightVolume),
});

export const formatNutritionWeight = (nutritions: NutritionAmounts, formatWithUnit: (value: number, unit: "gram" | "milliliter") => string): string => {
	const parts: string[] = [];
	if (toNutritionNumber(nutritions.weightMass) > 0) parts.push(formatWithUnit(nutritions.weightMass, "gram"));
	if (toNutritionNumber(nutritions.weightVolume) > 0) parts.push(formatWithUnit(nutritions.weightVolume, "milliliter"));
	return parts.join(" + ");
};

export const sumNutritions = (items: NutritionAmounts[]): NutritionAmounts => items.reduce(addNutritions, emptyNutritions());

const resolveSizeId = (size?: NutritionSource | string | string[] | null): string | undefined => {
	if (typeof size === "string") return size || undefined;
	if (Array.isArray(size)) return size.find((id) => typeof id === "string" && id)?.toString();
	if (isRecord(size) && typeof size.id === "string") return size.id;
	return undefined;
};

const findSizeById = (sizes: NutritionSource[] | null | undefined, sizeId?: string): NutritionSource | undefined => {
	if (!sizeId || !sizes?.length) return undefined;
	return sizes.find((size) => size.id === sizeId);
};

const resolveSelectedSize = (product?: NutritionProductInput | null, selectedSize?: NutritionSource | string | null): NutritionSource | null => {
	const sizes = product?.sizes ?? [];
	if (!sizes.length) return null;

	const selectedId = resolveSizeId(selectedSize);
	const fromSelected = findSizeById(sizes, selectedId) ?? (isRecord(selectedSize) && selectedId ? selectedSize : null);
	if (fromSelected) return fromSelected;

	return sizes.find((size) => size.is_default) ?? sizes[0] ?? null;
};

/** База: выбранный размер (даже с 0/null), иначе portion_nutrition. Поля самого товара не используем. */
export const resolveBaseNutritions = (product?: NutritionProductInput | null, selectedSize?: NutritionSource | string | null): NutritionAmounts => {
	const sizes = product?.sizes ?? [];
	if (sizes.length > 0) return pickNutritionSource(resolveSelectedSize(product, selectedSize));
	if (product?.portion_nutrition != null) return pickNutritionSource(product.portion_nutrition);
	return emptyNutritions();
};

const resolveModifierNutritionSource = (modifier: NutritionModifierInput, dishSize?: NutritionSource | string | null): NutritionSource => {
	const sizes = modifier.sizes ?? [];
	if (!sizes.length) return modifier;

	const modifierSizeId = resolveSizeId(modifier.size);
	const dishSizeId = resolveSizeId(dishSize);
	const matched = findSizeById(sizes, modifierSizeId) ?? (isRecord(modifier.size) ? modifier.size : undefined) ?? findSizeById(sizes, dishSizeId);
	return matched ?? modifier;
};

export const collectSelectedModifiers = (
	modifiers?: NutritionModifierInput[] | null,
	groupModifiersSelected?: NutritionGroupInput[] | null,
): NutritionModifierInput[] => [...(modifiers ?? []), ...(groupModifiersSelected ?? []).flatMap((group) => group.modifiers ?? [])];

export const calculateModifierNutritions = (modifier: NutritionModifierInput, dishSize?: NutritionSource | string | null): NutritionAmounts => {
	const amount = toNutritionNumber(modifier.amount);
	if (amount <= 0) return emptyNutritions();
	return scaleNutritions(pickNutritionSource(resolveModifierNutritionSource(modifier, dishSize)), amount);
};

export const calculateProductNutritions = ({
	product,
	selectedSize,
	modifiers,
	groupModifiersSelected,
	amount = 1,
}: CalculateNutritionsInput): NutritionAmounts => {
	if (!product) return emptyNutritions();

	const base = resolveBaseNutritions(product, selectedSize);
	const selectedModifiers = collectSelectedModifiers(modifiers, groupModifiersSelected);
	const withModifiers = selectedModifiers.reduce((acc, modifier) => addNutritions(acc, calculateModifierNutritions(modifier, selectedSize)), base);

	return scaleNutritions(withModifiers, Math.max(toNutritionNumber(amount), 0));
};

export const calculateCartItemNutritions = (
	item?: (NutritionSource & { amount?: number | null; weight_mass?: number | null; weight_volume?: number | null }) | null,
): NutritionAmounts => {
	if (!item) return emptyNutritions();

	const perUnit: NutritionAmounts = {
		energy_amount: toNutritionNumber(item.energy_amount),
		proteins_amount: toNutritionNumber(item.proteins_amount),
		fat_amount: toNutritionNumber(item.fat_amount),
		carbohydrates_amount: toNutritionNumber(item.carbohydrates_amount),
		weightMass: toNutritionNumber(item.weight_mass ?? item.weight),
		weightVolume: toNutritionNumber(item.weight_volume),
	};

	return scaleNutritions(perUnit, item.amount ?? 1);
};

/** КБЖУ позиции уже созданного заказа: бэк кладёт итоговые значения в `nutrition`. */
export const calculateOrderItemNutritions = (
	item?: {
		nutrition?: NutritionSource | null;
		amount?: number | null;
	} | null,
): NutritionAmounts => {
	if (!item) return emptyNutritions();
	return scaleNutritions(pickNutritionSource(item.nutrition), item.amount ?? 0);
};
